/**
 * Mémoire de l'achat, côté navigateur.
 *
 * Le produit n'a **qu'une** transaction, et elle ne laissait aucune trace :
 * l'identifiant vivait dans l'état local de la fenêtre de paiement. La fermer
 * — d'un geste, d'un retour, d'un rechargement — perdait le lien de
 * téléchargement pour de bon. L'acheteur avait payé trois mille francs et le
 * site continuait de lui proposer de payer.
 *
 * Ce qui est écrit ici : **l'identifiant de la transaction, le plan, la date**.
 * Rien d'autre. Pas de nom, pas de numéro, pas de résultat de diagnostic —
 * l'identifiant ne désigne personne, il désigne un paiement, et il ne sert
 * qu'à une chose : redemander au serveur où en est ce paiement et récupérer le
 * rapport qu'il a payé. C'est la contrepartie stricte de la règle qui veut
 * qu'aucune donnée nominative ne soit requise : sans nom ni compte, il faut
 * bien que le navigateur se souvienne de quelque chose, sinon l'acheteur n'a
 * aucun moyen de prouver son achat.
 *
 * Le stockage peut être refusé — navigation privée, réglage strict. Toutes les
 * opérations échouent alors en silence : on perd la reprise, jamais le
 * parcours.
 */

/**
 * Les trois offres achetables. `entreprise` est celle de l'axe dirigeant —
 * même mécanique de reprise et de mémorisation que les deux autres, seul le
 * montant diffère, et il vit dans le module de scoring partagé.
 */
export type PlanAchat = "report" | "bundle" | "entreprise";

export interface AchatMemorise {
  /** Identifiant de transaction rendu par le serveur à l'ouverture. */
  tx: string;
  plan: PlanAchat;
  /** Horodatage d'ouverture, pour ne pas garder une trace indéfiniment. */
  quand: number;
}

const CLE = "stratera.achat";

/**
 * Trente jours : au-delà, le lien de téléchargement a expiré côté serveur et
 * la trace ne sert plus qu'à encombrer. C'est aussi la durée qui couvre
 * largement les quatorze jours de la garantie de remboursement.
 */
const DUREE_MS = 30 * 24 * 60 * 60 * 1000;

function estPlan(valeur: unknown): valeur is PlanAchat {
  // Les trois plans, pas deux : « entreprise » manquait ici, si bien qu'un
  // dirigeant qui avait payé 15 000 F et fermé la fenêtre voyait sa trace
  // d'achat jetée à la relecture — et le site lui reproposait de payer.
  return valeur === "report" || valeur === "bundle" || valeur === "entreprise";
}

export function memoriserAchat(achat: AchatMemorise): void {
  try {
    window.localStorage.setItem(CLE, JSON.stringify(achat));
  } catch {
    /* Stockage refusé : la reprise sera impossible, le parcours continue. */
  }
}

export function lireAchat(): AchatMemorise | null {
  try {
    const brut = window.localStorage.getItem(CLE);
    if (!brut) return null;
    const objet = JSON.parse(brut) as Partial<AchatMemorise>;
    if (typeof objet.tx !== "string" || objet.tx === "" || !estPlan(objet.plan)) return null;
    const quand = typeof objet.quand === "number" ? objet.quand : 0;
    if (Date.now() - quand > DUREE_MS) {
      oublierAchat();
      return null;
    }
    return { tx: objet.tx, plan: objet.plan, quand };
  } catch {
    return null;
  }
}

export function oublierAchat(): void {
  try {
    window.localStorage.removeItem(CLE);
  } catch {
    /* Rien à faire : il n'y avait déjà rien à effacer. */
  }
}
